import { LineChart } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { InsightsReportResource } from "@octopusdeploy/octopus-server-client";
import { InsightsMetricsSplitValues } from "@octopusdeploy/portal-routes";
import { useEffect } from "react";
import { useAnalyticSimpleActionDispatch } from "~/analytics/Analytics";
import { InsightsReportLayout } from "~/areas/insights/components/InsightsReportLayout/InsightsReportLayout";
import { ReportMetricsLayout } from "~/areas/insights/components/Reports/ReportMetricsLayout/ReportMetricsLayout";
import type { ReportMetricsPageProps } from "~/areas/insights/components/Reports/ReportMetricsLayout/ReportMetricsLayout";
import { useReportFilters } from "~/areas/insights/components/Reports/ReportMetricsLayout/useReportFilters";
import { getChartAxisDateFormatter } from "../../dataTransformation/dateHelpers";
import { getChartReadyInsightsData, getYAxisUnitOfTimeKeys } from "../../dataTransformation/getChartReadyInsightsData";
import { getEvenlySpacedTickArray, getYAxisDataMax } from "../../dataTransformation/getYAxisDataMax";
import { formatDurationInWords } from "../../dataTransformation/stringHelpers";
import { insightsCadenceLookup, insightsCadenceXAxisTickInterval } from "../../insightsCadence";
import { InsightsTooltip } from "../ChartTooltips/InsightsTooltip/InsightsTooltip";
import { DownloadReportCsvButton } from "../DownloadReportCsvButton";
import { InsightsChartCard } from "../InsightsChartCard";
import { TimeToRecoveryTrendTables } from "./TrendTables/TimeToRecoveryTables";
type Props = ReportMetricsPageProps;
export function ReportMeanTimeToRecoveryInner({ report, bffResponse, split, cadence, busy }: Props) {
    const dispatchAction = useAnalyticSimpleActionDispatch();
    const chartData = getChartReadyInsightsData(bffResponse.Series);
    const { MeanTimeToRecoveryKey } = getYAxisUnitOfTimeKeys(chartData);
    const maxYAxisValue = getYAxisDataMax(chartData, `MeanTimeToRecovery.mean.${MeanTimeToRecoveryKey}`);
    const currentGranularity = insightsCadenceLookup[cadence].granularity;
    const dateFormatter = getChartAxisDateFormatter(currentGranularity);
    const showSeries = split !== InsightsMetricsSplitValues.None;
    useEffect(() => {
        dispatchAction("View Mean Time to Recovery Insights Report");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (<main>
            <section style={{ padding: `0 ${space[16]} ${space[32]}` }}>
                <InsightsChartCard height={500}>
                    <LineChart data={chartData} loading={Boolean(busy)} dataKey={`MeanTimeToRecovery.mean.${MeanTimeToRecoveryKey}`} yAxisLabel={`Avg. no. of ${MeanTimeToRecoveryKey.toLowerCase()}`} xAxisDataKey="StartOfInterval" xAxisType="category" yAxisType="number" yAxisDomain={[0, maxYAxisValue]} yAxisTickArray={getEvenlySpacedTickArray(maxYAxisValue)} xTickFormatter={dateFormatter} tooltip={<InsightsTooltip showSeries={showSeries} dataKey="MeanTimeToRecovery.mean.duration" valueFormatter={formatDurationInWords} cadence={cadence}/>} xAxisTickInterval={insightsCadenceXAxisTickInterval[cadence]} showLegend={showSeries}/>
                </InsightsChartCard>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: space[8] }}>
                    <DownloadReportCsvButton report={report}/>
                </div>
            </section>
            <section>
                <TimeToRecoveryTrendTables trends={bffResponse.MeanTimeToRecovery} spaceId={report.SpaceId}/>
            </section>
        </main>);
}
export function MeanTimeToRecoveryInsightsPage({ report }: {
    report: InsightsReportResource;
}) {
    const [reportFilter, setReportFilter] = useReportFilters();
    return (<InsightsReportLayout report={report} reportFilter={reportFilter}>
            <ReportMetricsLayout title="Mean time to recovery" report={report} trendKey="MeanTimeToRecovery" reportFilter={reportFilter} setReportFilter={setReportFilter}>
                {(props) => <ReportMeanTimeToRecoveryInner {...props}/>}
            </ReportMetricsLayout>
        </InsightsReportLayout>);
}
